import React, {useEffect, useRef, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useSpring, useTransition, animated} from '@react-spring/web';
import {Image} from '@sitecore-jss/sitecore-jss-react';
import LazyImage from '../../LazyImage/LazyImage';
import Text from '../JssOverridesText';

import Wrapper from './ScrollingText.styles';

import {useScrollPosition, useObserver, useDeviceResize} from '../../../hooks';
import {fillArrayWithData} from '../../../helpers/arrayHelpers';

const ScrollingElement = ({scrollingVanityTextElements = []}) => {
    const innerRef = useRef(null);
    const originalElRef = useRef(null);
    const [startLeftValue, setStartLeftValue] = useState(0);
    const [fillAmount, setFillAmount] = useState(0);

    const device = useDeviceResize();
    const scroll = useScrollPosition();
    const elInView = useObserver({ref: innerRef});

    const [animateProps, setAnimate] = useSpring(() => ({
        y: 0,
        config: {mass: 30, tension: 180, friction: 120},
    }));

    const calcMovement = useCallback((scroll, speed = 1) => {
        if (typeof window === 'undefined' || !innerRef.current) {
            return startLeftValue;
        }

        const win = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        const move = {
            width: win.width / 100,
            height: win.height / 100,
        };

        const rect = innerRef.current.getBoundingClientRect();
        const elementY = rect.top + window.scrollY;
        const elStartVisible = elementY - win.height;
        const scrollCalc = (scroll - elStartVisible) * (move.width / move.height);

        return +(startLeftValue + scrollCalc * speed).toFixed(0);
    }, [startLeftValue]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const startValue = window.innerWidth - (window.innerWidth * 2 + (window.innerWidth / 100) * 50);
            setStartLeftValue(startValue);
            setAnimate({y: startValue});
        }
    }, [setAnimate]);

    useEffect(() => {
        if (elInView) {
            setAnimate({y: calcMovement(scroll)});
        }
    }, [scroll, elInView, calcMovement]);

    useEffect(() => {
        if (originalElRef?.current?.offsetWidth > 0) {
            const fillToRender = Math.floor(window.innerWidth / originalElRef?.current?.offsetWidth);
            setFillAmount(fillToRender ? fillToRender * 5 : 5);
            const startValue = window.innerWidth - (window.innerWidth * 2 + (window.innerWidth / 100) * 50);
            setStartLeftValue(startValue);
            setAnimate({y: startValue});
        }
    }, [originalElRef, device, setAnimate]);

    const TextAndImage = () => {
        const [first = {}, second = {}] = scrollingVanityTextElements;

        return (
            <div>
                {first?.fields?.text && <Text field={first?.fields?.text} tag="span" className="vanity"/>}
                {first?.fields?.graphic && <LazyImage field={first?.fields?.graphic}/>}
                {second?.fields?.text && <Text field={second?.fields?.text} tag="span" className="vanity"/>}
                {second?.fields?.graphic && <LazyImage field={second?.fields?.graphic}/>}
            </div>
        );
    };

    if (scrollingVanityTextElements && scrollingVanityTextElements.length > 0) {
        return (
            <animated.div ref={innerRef} className="inner" style={{left: animateProps.y}}>
                <div ref={originalElRef}>
                    <TextAndImage key={'original'}/>
                </div>

                {fillAmount &&
                    fillArrayWithData({data: {}, fill: fillAmount})?.map((item, index) => {
                        return <TextAndImage key={`copy-${index}`} {...item} />;
                    })}
            </animated.div>
        );
    }

    return null;
};

const ScrollingText = (props) => (
    <Wrapper>
        {<ScrollingElement {...props} />}
    </Wrapper>
);

export default ScrollingText;

ScrollingText.propTypes = {
    scrollingVanityTextElements: PropTypes.array,
};

ScrollingElement.propTypes = {
    scrollingVanityTextElements: PropTypes.array,
};